import { ApiSaleOrderService } from 'services';
import { AxiosResponse } from 'axios';
import { ISort } from '../common/types';
import { formatSortingFields } from '../common/helpers';
import {
	IProviderBulkInsertInNetsuiteResultDTO,
	IProviderDTO,
	IProviderPage,
	IProviderPaymentCalendarCellDetailRequestDTO,
	IProviderPaymentCalendarDetailItemDTO,
	IProviderPaymentCalendarFiltersDTO,
	IProviderPaymentCalendarGridDTO,
	IProviderPaymentCalendarGroupList,
	IProviderPaymentCalendarViewConfig,
	IProviderPaymentCalendarViewConfigUpdate,
	ProviderPaymentDateRuleDTO,
	ProviderPaymentDateRuleUpsertDTO,
} from './types';

const endpoints = {
	list: 'providers',
	autocomplete: 'providers/autocomplete',
	paymentDateRule: 'providers/:id/payment-date-rule',
	getProviderPaymentCalendarViewConfig: 'providers/:id/payment-calendar-view-config',
	updateProviderPaymentCalendarViewConfig: 'providers/:id/payment-calendar-view-config',
	listAllProviderPaymentCalendarGroup: 'provider-payment-calendar-groups',
	grid: 'provider-payment-calendar/grid',
	cellDetail: 'provider-payment-calendar/cell-detail',
};

const buildProvidersParams = (
	page: number,
	size: number,
	sortingFields?: ISort[],
	filters?: Record<string, string | number | boolean>,
	search?: string,
): Record<string, string | number | boolean> => {
	const sorts = sortingFields ? formatSortingFields(sortingFields) : '';
	const params: Record<string, string | number | boolean> = { page, size };

	if (sorts.length > 0) {
		params.sort = sorts;
	} else {
		params.sort = 'fantasyName';
	}

	if (search) {
		params.search = search;
	}

	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (value === undefined || value === null || value === '') return;
			params[key] = value;
		});
	}

	return params;
};

export const getProviderList = (
	page = 1,
	size = 15,
	filters?: Record<string, string | number | boolean>,
	search = '',
	sortingFields?: ISort[],
	signal?: AbortSignal,
	agencyId?: number,
): Promise<AxiosResponse<IProviderPage>> =>
	ApiSaleOrderService.get(endpoints.list, {
		params: buildProvidersParams(page, size, sortingFields, filters, search),
		signal,
		agencyId,
	});

export const autocomplete = (value: string, limit = 50): Promise<AxiosResponse<IProviderDTO[]>> =>
	ApiSaleOrderService.get(endpoints.autocomplete, {
		params: {
			value,
			limit,
		},
	});

export const getProviderById = (id: string): Promise<AxiosResponse<IProviderDTO>> =>
	ApiSaleOrderService.get(`${endpoints.list}/${id}`);

export const putProvider = (body: Partial<IProviderDTO>): Promise<AxiosResponse<IProviderDTO>> =>
	ApiSaleOrderService.put(`${endpoints.list}/${body.id}`, body);

export const syncProviderWithNetsuite = (id: string): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.put(`${endpoints.list}/${id}/sync-with-netsuite`);

export const insertAllProviderInNetsuite = (): Promise<AxiosResponse<IProviderBulkInsertInNetsuiteResultDTO>> =>
	ApiSaleOrderService.post(`${endpoints.list}/insert-all-providers-in-netsuite`);

export const findPaymentDateRule = (providerId: string): Promise<AxiosResponse<ProviderPaymentDateRuleDTO>> =>
	ApiSaleOrderService.get(endpoints.paymentDateRule.replace(':id', providerId));

export const upsertPaymentDateRule = (
	providerId: string,
	data: ProviderPaymentDateRuleUpsertDTO,
): Promise<AxiosResponse<void>> => ApiSaleOrderService.put(endpoints.paymentDateRule.replace(':id', providerId), data);

export const getAllProviderPaymentCalendarGroup = (): Promise<AxiosResponse<IProviderPaymentCalendarGroupList[]>> =>
	ApiSaleOrderService.get(endpoints.listAllProviderPaymentCalendarGroup);

export const getProviderPaymentCalendarViewConfig = (
	providerId: string,
): Promise<AxiosResponse<IProviderPaymentCalendarViewConfig>> =>
	ApiSaleOrderService.get(endpoints.getProviderPaymentCalendarViewConfig.replace(':id', providerId));

export const updateProviderPaymentCalendarViewConfig = (
	providerId: string,
	data: IProviderPaymentCalendarViewConfigUpdate,
): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.put(endpoints.updateProviderPaymentCalendarViewConfig.replace(':id', providerId), data);

export const getProviderPaymentCalendarGrid = (
	filters: IProviderPaymentCalendarFiltersDTO,
): Promise<AxiosResponse<IProviderPaymentCalendarGridDTO>> => ApiSaleOrderService.post(endpoints.grid, filters);

export const getProviderPaymentCalendarCellDetail = (
	body: IProviderPaymentCalendarCellDetailRequestDTO,
): Promise<AxiosResponse<IProviderPaymentCalendarDetailItemDTO[]>> =>
	ApiSaleOrderService.post(endpoints.cellDetail, body);
